import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { getWorkerProfile, fileUrl } from '../utils/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
  ArrowLeft, ShieldCheck, Star, Briefcase, Inbox, Calendar, Mail, Phone, User,
} from 'lucide-react';

const ProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: u, isLoading } = useApi(() => getWorkerProfile(id), { immediate: true });

  if (isLoading && !u) {
    return <div className="flex items-center justify-center h-96"><LoadingSpinner size="lg" /></div>;
  }
  if (!u) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-400">Utilizador não encontrado.</p>
        <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600">Voltar</button>
      </div>
    );
  }

  const isWorker = u.role === 'worker';
  const initials = (u.full_name || 'U').trim().charAt(0).toUpperCase();
  const memberSince = u.created_at ? new Date(u.created_at).getFullYear() : '—';

  const stats = isWorker
    ? [
        { icon: Star, label: 'Avaliação', value: u.average_rating ?? 'N/A' },
        { icon: Briefcase, label: 'Trabalhos', value: u.completed_jobs ?? 0 },
        { icon: ShieldCheck, label: 'Verificação', value: u.verification_status === 'verified' ? 'Verificado' : 'Pendente' },
        { icon: Calendar, label: 'Membro desde', value: memberSince },
      ]
    : [
        { icon: Inbox, label: 'Pedidos', value: u.total_requests ?? 0 },
        { icon: Calendar, label: 'Membro desde', value: memberSince },
        { icon: ShieldCheck, label: 'Estado', value: u.status === 'active' ? 'Ativo' : 'Inativo' },
      ];

  return (
    <div className="space-y-6 max-w-4xl">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      {/* Cartão de perfil */}
      <div className="rounded-2xl overflow-hidden border border-gray-700 bg-gray-800">
        <div className="h-28 bg-gradient-to-br from-[#2e5bff] via-[#4aa8ff] to-[#00f1fe]" />
        <div className="px-6 pb-6 -mt-12">
          <div className="flex items-end gap-5 flex-wrap">
            {u.profile_picture ? (
              <img src={u.profile_picture.startsWith('http') ? u.profile_picture : fileUrl(u.profile_picture)} alt={u.full_name}
                className="w-24 h-24 rounded-2xl object-cover border-4 border-gray-800 shadow-lg" />
            ) : (
              <div className="w-24 h-24 rounded-2xl border-4 border-gray-800 flex items-center justify-center text-3xl font-bold text-white bg-gradient-to-br from-[#2e5bff] to-[#00f1fe]">
                {initials}
              </div>
            )}
            <div className="flex-1 min-w-[200px] pb-1">
              <div className="flex items-center gap-3 flex-wrap mb-1">
                <h1 className="text-2xl font-bold text-white">{u.full_name || 'Utilizador'}</h1>
                {isWorker
                  ? (u.verification_status === 'verified'
                      ? <span className="px-2 py-1 rounded-full text-xs font-medium border bg-green-500/20 text-green-400 border-green-500/30 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Verificado</span>
                      : <span className="px-2 py-1 rounded-full text-xs font-medium border bg-amber-500/20 text-amber-400 border-amber-500/30">Não verificado</span>)
                  : <span className="px-2 py-1 rounded-full text-xs font-medium border bg-purple-500/20 text-purple-400 border-purple-500/30 flex items-center gap-1"><User className="w-3 h-3" /> Cliente</span>}
              </div>
              <div className="flex items-center gap-4 flex-wrap text-sm text-gray-400">
                {isWorker && u.profession && (
                  <span className="px-2 py-1 rounded-full bg-[#00f1fe]/15 text-[#00f1fe] text-xs font-medium">{u.profession}</span>
                )}
                {u.email && <span className="flex items-center gap-1"><Mail className="w-4 h-4" /> {u.email}</span>}
                {u.phone_number && <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> {u.phone_number}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-2 text-[#00f1fe] mb-2"><s.icon className="w-5 h-5" /></div>
            <p className="text-xl font-bold text-white">{s.value}</p>
            <p className="text-gray-400 text-xs">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Bio */}
      <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
        <h3 className="text-white font-semibold mb-2">Sobre</h3>
        <p className="text-gray-400 text-sm leading-relaxed">{u.bio || 'Sem biografia.'}</p>
      </div>
    </div>
  );
};

export default ProfilePage;
